import { useFormContext, Path } from 'react-hook-form';
import { round, sumBy } from 'lodash';
import { realisation as rules } from 'further-ui/validations';
import { useState } from 'react';

export enum ConsiderationPerUnitType {
  Received = 'receivedConsiderationPerUnit',
  Escrow = 'escrowConsiderationPerUnit',
  Earnout = 'earnoutConsiderationPerUnit',
}

export enum ConsiderationTotalType {
  Received = 'receivedConsiderationTotal',
  Escrow = 'escrowConsiderationTotal',
  Earnout = 'earnoutConsiderationTotal',
  WithholdingTax = 'withholdingTaxPayable',
}

const perUnitToTotalMap: Record<
  ConsiderationPerUnitType,
  ConsiderationTotalType
> = {
  [ConsiderationPerUnitType.Received]: ConsiderationTotalType.Received,
  [ConsiderationPerUnitType.Escrow]: ConsiderationTotalType.Escrow,
  [ConsiderationPerUnitType.Earnout]: ConsiderationTotalType.Earnout,
};

const totalToPerUnitMap: Record<
  Exclude<ConsiderationTotalType, ConsiderationTotalType.WithholdingTax>,
  ConsiderationPerUnitType
> = {
  [ConsiderationTotalType.Received]: ConsiderationPerUnitType.Received,
  [ConsiderationTotalType.Escrow]: ConsiderationPerUnitType.Escrow,
  [ConsiderationTotalType.Earnout]: ConsiderationPerUnitType.Earnout,
};

type ConsiderationPath = Path<rules.CreateRealisationFormValues>;

const isPerUnit = (type: ConsiderationPerUnitType | ConsiderationTotalType) => {
  return Object.values(ConsiderationPerUnitType).includes(
    type as ConsiderationPerUnitType,
  );
};

const isTotal = (type: ConsiderationPerUnitType | ConsiderationTotalType) => {
  return Object.values(ConsiderationTotalType).includes(
    type as ConsiderationTotalType,
  );
};

const buildConsiderationPath = (
  holdingClassIndex: number,
  type: ConsiderationPerUnitType | ConsiderationTotalType,
): ConsiderationPath =>
  `holdingClassRealisation.${holdingClassIndex}.cashConsiderationAmounts.${type}` as ConsiderationPath;

const useConsiderationCalculations = () => {
  const { getValues, setValue } =
    useFormContext<rules.CreateRealisationFormValues>();

  const [activeField, setActiveField] = useState<
    ConsiderationPerUnitType | ConsiderationTotalType | null
  >(null);

  const holdingClassRealisation = getValues('holdingClassRealisation');

  const totalUnitsSold = round(sumBy(holdingClassRealisation, 'unitsSold'), 2);

  const perUnitCalculations = (value: number) => {
    return totalUnitsSold ? value / totalUnitsSold : 0;
  };

  const totalCalculations = (value: number) => {
    return totalUnitsSold ? value * totalUnitsSold : 0;
  };

  const onManualConsiderationChange = (
    value: number,
    type: ConsiderationPerUnitType | ConsiderationTotalType,
    index: number,
  ) => {
    if (type !== activeField) {
      return;
    }

    const holdingClass = holdingClassRealisation[index];
    const perUnitConsiderationPath = buildConsiderationPath(
      index,
      totalToPerUnitMap[type],
    );

    const totalConsiderationPath = buildConsiderationPath(
      index,
      perUnitToTotalMap[type],
    );

    if (isPerUnit(type)) {
      const total = holdingClass.unitsSold ? value * holdingClass.unitsSold : 0;
      setValue(totalConsiderationPath, total);
    } else if (isTotal(type)) {
      const perUnit = holdingClass.unitsSold
        ? value / holdingClass.unitsSold
        : 0;
      setValue(perUnitConsiderationPath, perUnit);
    }
  };

  const setPerHoldingClassConsiderationTotals = (
    value: number,
    type: ConsiderationPerUnitType,
  ) => {
    for (const [
      holdingClassIndex,
      holdingClass,
    ] of holdingClassRealisation.entries()) {
      const unitsSold = holdingClass.unitsSold;
      const total = unitsSold ? round(value * unitsSold, 2) : 0;

      const totalConsiderationPath = buildConsiderationPath(
        holdingClassIndex,
        perUnitToTotalMap[type],
      );

      setValue(totalConsiderationPath, total);

      const perUnitConsiderationPath = buildConsiderationPath(
        holdingClassIndex,
        type,
      );

      setValue(perUnitConsiderationPath, value);
    }
  };

  const setPerHoldingClassWithholdingTax = (value: number) => {
    for (const [
      holdingClassIndex,
      holdingClass,
    ] of holdingClassRealisation.entries()) {
      const withholdingTax = totalUnitsSold
        ? (value * (holdingClass.unitsSold ?? 0)) / totalUnitsSold
        : 0;

      const withholdingTaxPath = buildConsiderationPath(
        holdingClassIndex,
        ConsiderationTotalType.WithholdingTax,
      );

      setValue(withholdingTaxPath, withholdingTax);
    }
  };

  const setPerHoldingClassConsiderationPerUnit = (
    value: number,
    type: ConsiderationTotalType,
  ) => {
    for (const [
      holdingClassIndex,
      holdingClass,
    ] of holdingClassRealisation.entries()) {
      const unitsSold = holdingClass.unitsSold;
      const totalConsideration = unitsSold ? round(value * unitsSold, 2) : 0;

      const totalConsiderationPath = buildConsiderationPath(
        holdingClassIndex,
        type,
      );

      setValue(totalConsiderationPath, totalConsideration);

      const perUnitConsiderationPath = buildConsiderationPath(
        holdingClassIndex,
        totalToPerUnitMap[type],
      );

      setValue(perUnitConsiderationPath, value);
    }
  };

  const onWithholdingConsiderationTaxChange = (value: number) => {
    for (const [holdingClassIndex, _] of holdingClassRealisation.entries()) {
      const withholdingTaxPath = buildConsiderationPath(
        holdingClassIndex,
        ConsiderationTotalType.WithholdingTax,
      );

      setValue(withholdingTaxPath, value);

      setPerHoldingClassWithholdingTax(value);
    }
  };

  const onProRataPerUnitConsiderationChange = (
    index: number,
    value: number,
    type: ConsiderationPerUnitType,
  ) => {
    if (activeField === type) {
      const totalConsideration = totalCalculations(value);

      setValue(
        `consideration.${index}.proRataConsideration.${perUnitToTotalMap[type]}` as ConsiderationPath,
        totalConsideration,
      );

      setPerHoldingClassConsiderationTotals(value, type);
    }
  };

  const onProRataTotalConsiderationChange = (
    index: number,
    value: number,
    type: ConsiderationTotalType,
  ) => {
    if (activeField === type) {
      const perUnitConsideration = perUnitCalculations(value);

      setValue(
        `consideration.${index}.proRataConsideration.${totalToPerUnitMap[type]}` as ConsiderationPath,
        perUnitConsideration,
      );

      setPerHoldingClassConsiderationPerUnit(perUnitConsideration, type);
    }
  };

  return {
    perUnitCalculations,
    totalCalculations,
    onManualConsiderationChange,
    onProRataPerUnitConsiderationChange,
    onProRataTotalConsiderationChange,
    onWithholdingConsiderationTaxChange,
    setFieldFocus: setActiveField,
  };
};

export default useConsiderationCalculations;
